import { useEffect, useState } from 'react';
import { fetchUserData } from '../services/user-service';
import { AuthMode, PluginConfig } from '../models/plugin-config';
import { logout } from '../services/auth-service';
import { ApiResponse } from '../models/api-response';
import { User } from '../models/user';
import { usePluginEvent } from './usePluginEvent';
import { ClubMember } from '../models/club-member';
import { useClubContext } from '../context/ClubContext';
import { PLUGIN_EVENTS } from '../utils/pluginEvents';
import { OTP_METHODS, OtpMethod } from '../models/otp-methods';

export function useUser(config: PluginConfig | null, authMode?: AuthMode) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { club } = useClubContext();

  usePluginEvent(PLUGIN_EVENTS.REGISTRATION_SUCCESS, (clubMember: ClubMember) => {
    setUser({
      Name: clubMember.firstName,
      LoggedIn: true,
      Passport: String(clubMember.passport),
      Email: clubMember.email
    })
  });

  const onLogout = async (
    otpMethod: OtpMethod = OTP_METHODS.SMS,
    setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>> | null
  ): Promise<boolean> => {
    setIsLoggingOut(true);

    return logout(config, otpMethod, club?.bookingDomain)
      .then((response: ApiResponse<boolean>) => {
        const success = !!response && !response.Errors?.length;

        if (success) {
          setUser(null);
          setIsModalOpen?.(false);
        }

        return success;
      })
      .catch(() => false)
      .finally(() =>
        setTimeout(() => setIsLoggingOut(false), 500)
      );
  };

  useEffect(() => {
    if (!config || !club || authMode === "registration") return;

    setIsLoading(true);
    fetchUserData(config, club?.bookingDomain)
      .then((userResponse: ApiResponse<User>) => {
        if (userResponse.Data && userResponse.Data.LoggedIn && !userResponse.Errors?.length) {
          setUser(userResponse.Data)
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => { setIsLoading(false) }

      );
  }, [config, club]);

  return { user, setUser, onLogout, isLoading, error, isLoggingOut };
}
